/**
 * Facilities (ADR 0027, Entrega 1): catálogo de modalidades, formulários versionados (mecanismo
 * comum de forms.ts), central de chamados e cadastro corporativo de Locais.
 */
import { z } from 'zod';
import { defineFormSchemas, formFieldVisible, validateFormFieldValues } from './forms.ts';
import { localizedText } from './schemas.ts';

/** Grupos do menu "Solicitações" de Facilities (especificação V1, seção 2). */
export const FACILITIES_SERVICE_GROUPS = [
  'maintenance',
  'general_services',
  'spaces',
  'logistics',
  'assets',
  'materials',
  'travel',
  'other',
] as const;

/** Etapas do chamado (especificação V1, seção 5; aprovação e Compras entram na Entrega 5). */
export const FACILITIES_STATUSES = [
  'draft',
  'submitted',
  'in_triage',
  'pending_information',
  'in_progress',
  'awaiting_validation',
  'completed',
  'cancelled',
  'rejected',
] as const;
export type FacilitiesStatus = (typeof FACILITIES_STATUSES)[number];
export const FACILITIES_CLOSED: readonly FacilitiesStatus[] = [
  'completed',
  'cancelled',
  'rejected',
];

/** Recortes da fila de chamados que os cartões da Visão geral abrem (ADR 0097). */
export const FACILITIES_QUEUE_INDICATORS = ['safety_risk', 'unassigned', 'overdue'] as const;
export type FacilitiesQueueIndicator = (typeof FACILITIES_QUEUE_INDICATORS)[number];
export const facilitiesQueueIndicator = z.enum(FACILITIES_QUEUE_INDICATORS);

/** Prioridade (urgência do atendimento), separada do risco à segurança. */
export const FACILITIES_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
/** Impacto na operação. */
export const FACILITIES_IMPACTS = ['low', 'medium', 'high', 'critical'] as const;
/**
 * Origem: aberto pela própria pessoa, registrado pela equipe em nome de alguém, ou pendência
 * anterior à implantação ("Transição de Facilities").
 */
export const FACILITIES_ORIGINS = ['direct', 'on_behalf', 'transition'] as const;
/** Como o chamado terminou: com aceite, encerramento administrativo, cancelado ou rejeitado. */
export const FACILITIES_CLOSE_KINDS = [
  'accepted',
  'administrative',
  'cancelled',
  'rejected',
] as const;
export const FACILITIES_VISIBILITY = ['internal', 'shared'] as const;
/** Prazo para reabrir um chamado concluído (dias corridos). */
export const FACILITIES_REOPEN_DAYS = 30;

/** Níveis do cadastro de Locais, abaixo da filial; os intermediários são opcionais. */
export const LOCATION_KINDS = ['building', 'floor', 'space'] as const;
export type LocationKind = (typeof LOCATION_KINDS)[number];
/** Que níveis podem ficar acima de cada um (vazio = direto na filial). */
export const LOCATION_PARENT_KINDS: Record<LocationKind, readonly LocationKind[]> = {
  building: [],
  floor: ['building'],
  space: ['building', 'floor'],
};

/**
 * Tipos de campo dos formulários de Facilities. "location" aponta para o cadastro de Locais;
 * "attachment" indica um documento ou evidência, enviado como anexo do chamado.
 */
export const FACILITIES_FIELD_TYPES = [
  'text',
  'long_text',
  'number',
  'date',
  'money',
  'select',
  'multi_select',
  'checkbox',
  'user',
  'department',
  'cost_center',
  'partner',
  'location',
  'asset',
  'attachment',
] as const;
export type FacilitiesFieldType = (typeof FACILITIES_FIELD_TYPES)[number];
export const FACILITIES_LOOKUP_TYPES: readonly FacilitiesFieldType[] = [
  'user',
  'department',
  'cost_center',
  'partner',
  'location',
  'asset',
];

const facilitiesForms = defineFormSchemas(FACILITIES_FIELD_TYPES);
export const facilitiesFieldInput = facilitiesForms.field;
export type FacilitiesField = z.infer<typeof facilitiesFieldInput>;
export const facilitiesFormSchemaInput = facilitiesForms.schema;
export type FacilitiesFormSchema = z.infer<typeof facilitiesFormSchemaInput>;

export function facilitiesFieldVisible(
  field: FacilitiesField,
  values: Record<string, unknown>,
): boolean {
  return formFieldVisible(field, values);
}

export function validateFacilitiesFieldValues(
  schema: FacilitiesFormSchema,
  values: Record<string, unknown>,
): { path: string; message: string }[] {
  return validateFormFieldValues(schema, values);
}

/** Dados da modalidade que a gestão de Facilities ajusta pela tela. */
export const facilitiesServiceInput = z.object({
  name: localizedText,
  description: z
    .object({ 'pt-BR': z.string().trim().max(1000), en: z.string().trim().max(1000) })
    .default({ 'pt-BR': '', en: '' }),
  isActive: z.boolean(),
  isReady: z.boolean(),
  sortOrder: z.number().int().min(1).max(999),
});

// ----- Chamados -----

const optional = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const optionalId = z
  .uuid()
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const optionalDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();

/**
 * Abrir o rascunho: modalidade, empresa, filial (unidade) e título. A equipe pode registrar em
 * nome de outra pessoa ou uma pendência da transição (então informa quem é o solicitante).
 */
export const facilitiesRequestCreateInput = z
  .object({
    serviceId: z.uuid(),
    legalEntityId: z.uuid(),
    branchId: z.uuid(),
    title: z.string().trim().min(1).max(150),
    origin: z.enum(FACILITIES_ORIGINS).default('direct'),
    requesterUserId: z.uuid().optional(),
  })
  .refine((v) => v.origin === 'direct' || !!v.requesterUserId, {
    path: ['requesterUserId'],
    message: 'Informe o solicitante',
  });

/** Campos base do chamado, os dados da transição e as respostas dos campos específicos. */
export const facilitiesRequestDraftInput = z
  .object({
    branchId: z.uuid(),
    locationId: optionalId,
    title: z.string().trim().min(1).max(150),
    description: optional(10000),
    justification: optional(5000),
    departmentId: optionalId,
    costCenterId: optionalId,
    priority: z.enum(FACILITIES_PRIORITIES).default('normal'),
    impact: z.enum(FACILITIES_IMPACTS).default('low'),
    safetyRisk: z.boolean().default(false),
    safetyDetail: optional(2000),
    neededBy: optionalDate,
    accessWindow: optional(500),
    fieldValues: z.record(z.string(), z.unknown()).default({}),
    // Transição de Facilities (pendência anterior à implantação).
    currentOwnerUserId: optionalId,
    futureOwnerUserId: optionalId,
    identifiedOn: optionalDate,
    transitionDueDate: optionalDate,
    transitionStatus: optional(2000),
    nextAction: optional(2000),
    transferNotes: optional(4000),
  })
  .refine((v) => !v.safetyRisk || !!v.safetyDetail, {
    path: ['safetyDetail'],
    message: 'Descreva o risco',
  });
export type FacilitiesRequestDraftInput = z.infer<typeof facilitiesRequestDraftInput>;

/** O que falta para enviar (campos base); os específicos são conferidos pelo formulário. */
export function facilitiesSubmitIssues(r: {
  origin: string;
  departmentId?: string | null;
  description?: string | null;
  safetyRisk?: boolean;
  safetyDetail?: string | null;
  currentOwnerUserId?: string | null;
  futureOwnerUserId?: string | null;
  identifiedOn?: string | null;
  transitionDueDate?: string | null;
  transitionStatus?: string | null;
  nextAction?: string | null;
}): { path: string; message: string }[] {
  const issues: { path: string; message: string }[] = [];
  const need = (path: string, ok: unknown) => {
    if (!ok) issues.push({ path, message: 'Obrigatório' });
  };
  need('departmentId', r.departmentId);
  need('description', r.description);
  if (r.safetyRisk) need('safetyDetail', r.safetyDetail);
  if (r.origin === 'transition') {
    need('currentOwnerUserId', r.currentOwnerUserId);
    need('futureOwnerUserId', r.futureOwnerUserId);
    need('identifiedOn', r.identifiedOn);
    need('transitionDueDate', r.transitionDueDate);
    need('transitionStatus', r.transitionStatus);
    need('nextAction', r.nextAction);
  }
  return issues;
}

const reason = z.string().trim().min(5).max(2000);

/** Cancelar, rejeitar, devolver, reabrir, encerrar administrativamente: sempre com motivo. */
export const facilitiesReasonInput = z.object({ reason });
/** Pedido de complementação e a resposta de quem pediu. */
export const facilitiesMessageInput = z.object({ message: z.string().trim().min(5).max(4000) });
/** Comentário: a equipe escolhe interno ou compartilhado; quem é de fora só compartilha. */
export const facilitiesCommentInput = z.object({
  note: z.string().trim().min(1).max(4000),
  visibility: z.enum(FACILITIES_VISIBILITY).default('internal'),
});
/** Responsável: trocar quem já estava exige justificativa (fica no histórico). */
export const facilitiesAssignInput = z.object({
  assigneeUserId: z.uuid().nullable(),
  reason: optional(2000),
});
/** Confirmar ou ajustar a classificação (prioridade, impacto e risco), com motivo se mudar. */
export const facilitiesClassifyInput = z
  .object({
    priority: z.enum(FACILITIES_PRIORITIES),
    impact: z.enum(FACILITIES_IMPACTS),
    safetyRisk: z.boolean(),
    safetyDetail: optional(2000),
    reason: optional(2000),
  })
  .refine((v) => !v.safetyRisk || !!v.safetyDetail, {
    path: ['safetyDetail'],
    message: 'Descreva o risco',
  });
/** Prazo combinado na triagem (ou nenhum). Mudar um prazo já combinado exige motivo. */
export const facilitiesScheduleInput = z.object({
  dueDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
  reason: optional(2000),
});
/** Dados da Transição de Facilities atualizados pela equipe durante o atendimento. */
export const facilitiesTransitionInput = z.object({
  currentOwnerUserId: z.uuid(),
  futureOwnerUserId: z.uuid(),
  transitionDueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  transitionStatus: z.string().trim().min(1).max(2000),
  nextAction: z.string().trim().min(1).max(2000),
  transferNotes: optional(4000),
});
/** Execução concluída: o que foi feito (quem pediu vê e valida). */
export const facilitiesFinishInput = z.object({ resolution: z.string().trim().min(5).max(4000) });
/** Aceite de quem pediu, com observação opcional. */
export const facilitiesAcceptInput = z.object({ note: optional(2000) });

// ----- Locais -----

export const locationInput = z.object({
  branchId: z.uuid(),
  parentId: optionalId,
  kind: z.enum(LOCATION_KINDS),
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9_.-]{0,29}$/, 'Use letras, números, ponto, hífen ou _ (até 30)'),
  name: z.string().trim().min(1).max(200),
  capacity: z.number().int().min(1).max(100000).nullable().optional(),
  notes: optional(2000),
  isActive: z.boolean().default(true),
  /** Só o ambiente pode ser estoque (materiais de Facilities, Entrega 4). */
  isStorage: z.boolean().default(false),
});
export type LocationInput = z.infer<typeof locationInput>;
